import { Group, Modal, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import UploadFile from 'components/files/UploadFile';
import UploadFiles from 'components/files/upload/UploadFiles';
import type { IFileState } from 'components/files/upload/types';
import { DateInput } from 'components/inputs/DateInput';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import { useMemo } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/** Inputs for the modal-contained HR term execution form. */
interface HrExecuteTermModalProps {
  term: any;
  fileCategories: any[];
  opened: boolean;
  onClose: () => void;
  onExecuted: (response: unknown) => void | Promise<void>;
  onConflict: () => void;
}

/** Returns today's local calendar date without converting through UTC. */
function todayDate() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/** Mirrors the PHP execution-date recurrence calculation for the modal preview. */
export function calculateHrRecurrencePreview(
  executedOn: string,
  recurrence?: { quantity: number; unit: string } | null
) {
  if (!executedOn || !recurrence || recurrence.quantity < 1) {
    return null;
  }
  const match = executedOn.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  let result: Date;
  if (recurrence.unit === 'days' || recurrence.unit === 'weeks') {
    const days = recurrence.quantity * (recurrence.unit === 'weeks' ? 7 : 1);
    result = new Date(Date.UTC(year, month - 1, day + days));
  } else {
    const monthOffset = recurrence.unit === 'months' ? recurrence.quantity : recurrence.quantity * 12;
    const firstOfTarget = new Date(Date.UTC(year, month - 1 + monthOffset, 1));
    const lastDay = new Date(Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0)).getUTCDate();
    result = new Date(Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth(), Math.min(day, lastDay)));
  }

  return result.toISOString().slice(0, 10);
}

/** Shared direct-execution modal used by HR person and HR terms surfaces. */
export default function HrExecuteTermModal({
  term,
  fileCategories,
  opened,
  onClose,
  onExecuted,
  onConflict,
}: HrExecuteTermModalProps) {
  const { getAction } = useApi();
  const form = useForm({
    initialValues: {
      executedOn: todayDate(),
      nextDueDate: '',
      note: '',
      proofFiles: [] as IFileState[],
      proofCategoryId: '',
      certificateNumber: term.certificateNumber ?? '',
      certificateFileId: term.certificateFileId ?? '',
      stampFileId: term.stampFileId ?? '',
    },
  });
  const executionPreview = useMemo(
    () => calculateHrRecurrencePreview(form.values.executedOn, term.recurrence),
    [form.values.executedOn, term.recurrence]
  );
  const proofUploadsPending = useMemo(
    () => form.values.proofFiles.some((file) => !file.finalized || !file.fileId),
    [form.values.proofFiles]
  );

  const submit = form.onSubmit(async (values) => {
    if (values.proofFiles.length > 0) {
      if (values.proofFiles.some((file) => !file.finalized || !file.fileId)) {
        form.setFieldError('proofFiles', 'Počkajte na dokončenie nahrávania všetkých príloh');
        return;
      }
      if (!values.proofCategoryId) {
        form.setFieldError('proofCategoryId', 'Vyberte kategóriu príloh');
        return;
      }
    }

    try {
      const response = await getAction('HrExecuteTerm')({
        parameters: { termId: String(term.termId) },
        payload: {
          version: term.version,
          executedOn: values.executedOn,
          nextDueDate: values.nextDueDate || undefined,
          note: values.note || undefined,
          proofFiles:
            values.proofFiles.length > 0
              ? values.proofFiles.map((file) => ({
                  fileId: file.fileId!,
                  fileName: file.fileName,
                  categoryId: Number(values.proofCategoryId),
                }))
              : undefined,
          certificateNumber: values.certificateNumber || undefined,
          certificateFileId: values.certificateFileId || undefined,
          stampFileId: values.stampFileId || undefined,
        },
      });
      notifications.show({
        title: 'HR termín bol aktualizovaný',
        message: 'Výsledok bol uložený a načítaný zo servera.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      onClose();
      await onExecuted(response);
    } catch (caught: any) {
      if (caught?.response?.status === 409) {
        onConflict();
      } else {
        panic(caught as Error);
      }
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Vykonať termín" size="lg">
      <form onSubmit={submit}>
        <Stack>
          <DateInput size="md" label="Dátum vykonania" withAsterisk {...form.getInputProps('executedOn')} />
          <DateInput size="md" label="Vypočítaný nasledujúci termín" value={executionPreview ?? ''} readOnly />
          <DateInput size="md" label="Nasledujúci dátum (ak sa má prepísať)" {...form.getInputProps('nextDueDate')} />
          <Textarea size="md" label="Poznámka" {...form.getInputProps('note')} />
          {term.actionPermissions?.accessFiles && (
            <>
              <Stack spacing={4}>
                <P2Regular color="gray.6">Prílohy</P2Regular>
                <UploadFiles
                  w="100%"
                  inputSize="md"
                  onChange={(proofFiles = []) => {
                    form.setFieldValue('proofFiles', proofFiles);
                    if (!proofFiles.some((file) => !file.finalized || !file.fileId)) {
                      form.clearFieldError('proofFiles');
                    }
                    if (proofFiles.length === 0) {
                      form.clearFieldError('proofCategoryId');
                    }
                  }}
                  error={form.errors.proofFiles}
                />
              </Stack>
              {form.values.proofFiles.length > 0 && (
                <Select
                  size="md"
                  label="Kategória príloh"
                  withAsterisk
                  data={fileCategories.map((category) => ({
                    value: String(category.categoryId),
                    label: category.name ?? `Kategória ${category.categoryId}`,
                  }))}
                  {...form.getInputProps('proofCategoryId')}
                />
              )}
            </>
          )}
          {term.kind === 'technician-certificate' && (
            <>
              <TextInput size="md" label="Číslo osvedčenia" withAsterisk {...form.getInputProps('certificateNumber')} />
              <UploadFile label="Osvedčenie" {...form.getInputProps('certificateFileId')} />
              <UploadFile label="Pečiatka" {...form.getInputProps('stampFileId')} />
            </>
          )}
          <Group position="right" mt="md">
            <Button variant="subtle-gray" type="button" onClick={onClose}>
              Zrušiť
            </Button>
            <Button
              variant="primary"
              type="submit"
              leftIcon={<IconCheck stroke={1.5} />}
              disabled={proofUploadsPending}
            >
              Potvrdiť
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
